import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { SIGNUP_INTERESTS } from "@/lib/pathways";
import { PROFILE_TYPES } from "@/lib/profileTypes";
import { getStoredInviter, clearStoredInviter } from "@/lib/invites";
import { Check } from "lucide-react";
import Seo from "@/components/Seo";

export default function Register() {
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [profileType, setProfileType] = useState("");
  const [interests, setInterests] = useState([]);
  const [covenantAccepted, setCovenantAccepted] = useState(false);
  const [step, setStep] = useState(1); // 1 = who are you, 2 = interests, 3 = account
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const toggleInterest = (key) =>
    setInterests((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  const submit = async (e) => {
    e.preventDefault();
    if (!covenantAccepted) {
      setError("Please accept the community covenant to join.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const inviteContext = getStoredInviter();
      const { data } = await api.post("/auth/register", {
        name, email, password, interests, profile_type: profileType || null,
        covenant_accepted: true,
        ...(inviteContext?.id ? {
          invited_by: inviteContext.id,
          ...(inviteContext.movie_id ? { invite_movie_id: inviteContext.movie_id } : {}),
        } : {}),
      });
      if (inviteContext) clearStoredInviter();
      setUser(data);
      navigate("/feed");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not create your account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-12">
      <Seo
        title="Join the community · Jesus Just Us"
        description="Create a free Jesus Just Us account. Join prayer groups, share testimonies, RSVP to gatherings, and walk with believers."
        path="/register"
      />
      <div className="w-full max-w-2xl jj-card p-8 sm:p-10 fade-up">
        <div className="overline text-burgundy">Begin together</div>
        <h1 className="font-serif text-4xl text-jjblue mt-2">
          {step === 1 && "Who are you?"}
          {step === 2 && "What brings you here?"}
          {step === 3 && "Almost there"}
        </h1>
        <p className="mt-2 text-[var(--jj-text-secondary)] text-sm">
          Step {step} of 3
          {step === 1 && " · This helps the community find and connect with you."}
          {step === 2 && " · Pick what matters to you. We'll tailor your experience."}
          {step === 3 && " · A quick last step to create your account."}
        </p>

        {step === 1 && (
          <>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {PROFILE_TYPES.map((t) => {
                const Icon = t.icon;
                const on = profileType === t.key;
                return (
                  <button type="button" key={t.key} onClick={() => setProfileType(t.key)}
                    data-testid={`profile-type-${t.key}`}
                    className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                      on
                        ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)] shadow-md"
                        : "bg-cream-surface border-jj hover:border-[var(--jj-gold)]"
                    }`}>
                    <Icon className={`h-5 w-5 ${on ? "text-cream" : "text-burgundy"}`} strokeWidth={1.6} />
                    <div className={`font-serif text-lg mt-3 ${on ? "text-cream" : "text-jjblue"}`}>{t.label}</div>
                    <div className={`text-xs mt-1 leading-snug ${on ? "text-cream/85" : "text-[var(--jj-text-secondary)]"}`}>{t.blurb}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <p className="text-sm text-[var(--jj-muted)]">{profileType ? `Selected: ${PROFILE_TYPES.find(p => p.key === profileType)?.label}` : "Pick one (optional)"}</p>
              <button onClick={() => setStep(2)} className="btn-primary" data-testid="reg-step1-next">Continue</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SIGNUP_INTERESTS.map((i) => {
                const Icon = i.icon;
                const on = interests.includes(i.key);
                return (
                  <button type="button" key={i.key} onClick={() => toggleInterest(i.key)}
                    data-testid={`interest-${i.key}`}
                    className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                      on
                        ? "bg-[var(--jj-burgundy)] text-cream border-[var(--jj-burgundy)] shadow-md"
                        : "bg-cream-surface border-jj hover:border-[var(--jj-gold)]"
                    }`}>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl leading-none">{i.emoji}</span>
                      <Icon className={`h-4 w-4 ${on ? "text-cream" : "text-burgundy"}`} strokeWidth={1.6} />
                    </div>
                    <div className={`font-serif text-lg mt-3 ${on ? "text-cream" : "text-jjblue"}`}>{i.label}</div>
                    {on && (
                      <div className="mt-1 text-xs flex items-center gap-1 text-cream/90">
                        <Check className="h-3 w-3" /> Selected
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setStep(1)} className="btn-secondary !py-2 !px-4 text-sm" data-testid="reg-step2-back">Back</button>
              <button onClick={() => setStep(3)} className="btn-primary" data-testid="reg-step2-next">Continue</button>
            </div>
          </>
        )}

        {step === 3 && (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm text-jjblue/80">Name</label>
              <input className="input-jj mt-1" required value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">Email</label>
              <input className="input-jj mt-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email" />
            </div>
            <div>
              <label className="text-sm text-jjblue/80">Password</label>
              <input className="input-jj mt-1" type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password" />
            </div>

            <label className="flex items-start gap-3 pt-2 select-none cursor-pointer" data-testid="reg-covenant-row">
              <input
                type="checkbox"
                checked={covenantAccepted}
                onChange={(e) => setCovenantAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 accent-burgundy"
                data-testid="reg-covenant"
              />
              <span className="text-xs text-jjblue/85 leading-relaxed">
                I agree to our covenant: <em>I will share only what is inspiring, uplifting,
                motivational, or honest information. I will not gossip, slander, manipulate, or
                post content that doesn&rsquo;t honor Christ or this family.</em>
              </span>
            </label>

            {error && <div className="text-sm text-[var(--jj-burgundy)]" data-testid="reg-error">{error}</div>}
            <div className="flex items-center justify-between pt-2">
              <button type="button" onClick={() => setStep(2)} className="btn-secondary !py-2 !px-4 text-sm" data-testid="reg-step3-back">Back</button>
              <button disabled={busy} className="btn-primary" data-testid="reg-submit">{busy ? "Creating…" : "Create my account"}</button>
            </div>
          </form>
        )}

        <p className="mt-6 text-sm text-[var(--jj-text-secondary)]">
          Already a member? <Link to="/login" className="text-burgundy font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
